import React, { FC, useState, useCallback, FormEvent, useEffect } from "react";
import Popup from "../../Popup/Popup";
import {
  Label,
  TextField,
  IStackTokens,
  Stack,
  mergeStyles,
  Text,
  IconButton,
  IPersonaProps,
  FontIcon,
  ITextFieldStyleProps,
  ITextFieldStyles,
  IStyleFunctionOrObject,
} from "@fluentui/react";
import { HorizontalStack } from "../../Stack";
import { ProjectService } from "../../../services/ProjectService";
import { response } from "../../../models";
import { useTranslation } from "react-i18next";
import Loader from "../../Loader";
import { useAppDispatch } from "../../../hooks/hooks";
import { getAllProjects } from "../../../modules/redux/Projects/project";
import { useNavigate } from "react-router-dom";
import { colorPallete } from "../../../assets/color";
import { MailTextBox } from "../../MailTextBox";
import CustomToggle from "../../CustomToggle";
import SuccessIcon from "../../../assets/Icons/svg/successIcon";
import ErrorIcon from "../../../assets/Icons/svg/errorIcon";

const buttonStackStyles = {
  root: {
    padding: "15px",
    justifyContent: "flex-end",
    alignSelf: "end",
    gap: "5px",
    background: "white",
    height: "20%",
    width: "100%",
    borderRadius: "0 0 8px 8px",
  },
};
const horizontalStackTokens: IStackTokens = {
  childrenGap: "2%",
};

const uploadModal = mergeStyles({
  display: "flex",
  width: "100%",
  height: "120px",
  backgroundColor: "white",
  padding: "20px 25px",
});

const FolderNameStyles: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  root: {
    height: "44px",
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    width: "100%",
    padding: "5px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
  },
  field: {
    backgroundColor: "#F8F8F8",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
    selectors: {
      "::placeholder": {
        color: "#717070",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        fontFamily: "Poppins",
      },
      "::-webkit-inner-spin-button": {
        display: "none",
      },
      "-moz-appearance: textfield": {
        display: "none",
      },
    },
  },
};

const CounterPartyStyles: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  root: {
    height: "44px",
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    width: "100%",
    padding: "5px",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
  },
  field: {
    backgroundColor: "#F8F8F8",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
    selectors: {
      "::placeholder": {
        color: "#717070",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        fontFamily: "Poppins",
      },
    },
  },
};

const CompletionDateStyles: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  root: {
    height: "44px",
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    width: "100%",
    padding: "5px 3px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
  },
  field: {
    backgroundColor: "#F8F8F8",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
    textTransform: "uppercase",
    selectors: {
      "::placeholder": {
        color: "#717070",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        fontFamily: "Poppins",
        textTransform: "uppercase",
      },
    },
  },
};

const projectValueStyles: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  root: {
    height: "44px",
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    width: "100%",
    padding: "5px 3px",
    border: "1px solid #E0E0E0",

    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
  },
  field: {
    backgroundColor: "#F8F8F8",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
    selectors: {
      "::placeholder": {
        color: "#717070",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        fontFamily: "Poppins",
      },
    },
  },
};

const DescriptionStyles: IStyleFunctionOrObject<
  ITextFieldStyleProps,
  ITextFieldStyles
> = {
  root: {
    height: "90px",
    backgroundColor: "#F8F8F8",
    borderRadius: "8px",
    width: "100%",
    padding: "5px",
    border: "1px solid #E0E0E0",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
  },
  field: {
    backgroundColor: "#F8F8F8",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "24px",
    fontFamily: "Poppins",
    height: "80px",
    selectors: {
      "::placeholder": {
        color: "#717070",
        fontSize: "16px",
        fontWeight: 400,
        lineHeight: "24px",
        fontFamily: "Poppins",
      },
    },
  },
};

export const CreateProjectModal: FC<any> = (props) => {
  const [folderName, setFolderName] = useState("");
  const [projectOwner, setProjectOwner] = useState("");
  const [projectOwnerError, setProjectOwnerError] = useState(false);
  const [counterParty, setCounterParty] = useState("");
  const [description, setDescription] = useState("");
  const [clientNumber, setClientNumber] = useState("");
  const [clientMatter, setClientMatter] = useState("");
  const [completionDate, setCompletionDate] = useState("");
  const [errorMesssage, setErrorMesssage] = useState("");
  const [folderNameError, setFolderNameError] = useState(false);
  const [counterPartyError, setCounterPartyError] = useState(false);
  const [projectExist, setProjectExist] = useState(false);
  const [modal, setModal] = useState<boolean>(false);
  const [upload, setUpload] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  const [estimatedClosingValue, setEstimatedClosingValue] = useState("");
  const [estimatedCompletetionValueError, setEstimatedCompletetionValueError] =
    useState(false);
  const [active, setActive] = useState<boolean>(true);
  const dispatch = useAppDispatch();
  const user = JSON.parse(localStorage.getItem("user")!);
  const [currentSelectedItems, setCurrentSelectedItems] = useState<
    IPersonaProps[]
  >([]);

  const [textFields, setTextFields] = useState<string[]>([""]);

  const addCounterPartyField = () => {
    setTextFields([...textFields, ""]);
  };
  const updatedTextFields = [...textFields];
  const removeTextField = (index: number) => {
    updatedTextFields.splice(index, 1);
    setTextFields(updatedTextFields);
  };

  useEffect(() => {}, [textFields]);

  const handleTextFieldChange = (index: number, value: string) => {
    updatedTextFields[index] = value;
    if (value.length === 0) {
      setCounterPartyError(true);
    } else {
      setCounterPartyError(false);
    }

    setTextFields(updatedTextFields);
  };

  const onChangeFolderNameTextField = useCallback(
    (
      event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      if (newValue?.length === 0) {
        setFolderNameError(true);
      } else {
        setProjectExist(false);
        setFolderNameError(false);
      }
      setFolderName(newValue || "");
    },
    []
  );

  const onChangeEstimatedClosingValueTextField = useCallback(
    (
      event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      setEstimatedClosingValue(newValue ?? "");
    },
    []
  );
  const onChangeDateField = useCallback(
    (
      event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
      newValue?: string
    ) => {
      setCompletionDate(newValue || "");
    },
    []
  );

  function onChangeMultiLine(
    event: FormEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue?: string
  ) {
    setDescription(newValue || "");
  }

  const hideModal = props.hideModal;
  const navigate = useNavigate();

  const redirectFolder = (projectId: number | undefined) => {
    navigate(`/home/${projectId}`, {
      state: {
        created: true,
      },
    });
  };

  const displayDocUpload = props.displayDoc;

  const clearedTextFields = updatedTextFields.map(() => "");
  const counterPartiesArray = textFields.filter((text) => text.trim() !== "");
  const counterParties = counterPartiesArray.join(";");

  async function createProject() {
    if (folderName.length === 0) {
      setFolderNameError(true);
    }
    if (updatedTextFields[0].length === 0) {
      setCounterPartyError(true);
      return;
    }

    if (folderName.length !== 0 && updatedTextFields[0].length !== 0) {
      const project: any = {
        projectName: folderName.trim(),
        counterParties: counterParties,
        description: description ? description : " ",
        estimatedCompletionDate: completionDate,
        projectOwner: user?.email,
        projectValue:
          estimatedClosingValue.length > 0
            ? estimatedClosingValue.split("$")[1].replace(/,/g, "")
            : 0,
        projectStatus: active ? "Active" : "Closed",
        clientNumber: Number(clientNumber),
        clientMatter: Number(clientMatter),
      };
      setModal(true);
      await ProjectService.create(project).then((res: response) => {
        setUpload(true);
        if (res.message === "Name already exists") {
          setMessage("project_already_exist");
        } else if (res.code === 200) {
          setMessage("create_project_success");
          props.setProjectLimit?.((prevCount: number) => prevCount - 1);
        } else {
          if (res.data?.message) {
            setMessage(res.data.message);
          } else {
            setMessage(res.message);
          }
          setTimeout(() => {
            setModal(false);
            setUpload(false);
          }, 2000);
          return;
        }
        dispatch(
          getAllProjects({
            limit: 30,
            offset: 0,
          })
        );
        setTimeout(() => {
          setModal(false);
          setUpload(false);
          if (!displayDocUpload && res.message !== "Name already exists") {
            const projectId = res.data?.id;
            redirectFolder(projectId);
          } else if (displayDocUpload) {
            hideModal();
            displayDocUpload(folderName.trim());
          }
        }, 2000);
      });
      setFolderName("");
      setCounterParty("");
      setCompletionDate("");
      setDescription("");
      setTextFields(clearedTextFields);
      setClientNumber("");
      setClientMatter("");
    }
  }
  const closeProject = () => {
    hideModal();
    setFolderName("");
    setCounterParty("");
    setErrorMesssage("");
    setCompletionDate("");
    setDescription("");
    setCounterPartyError(false);
    setFolderNameError(false);
    setProjectOwner("");
    setProjectOwnerError(false);
    setTextFields(clearedTextFields);
    setEstimatedClosingValue("");
    setEstimatedCompletetionValueError(false);
    setClientNumber("");
    setClientMatter("");
    displayDocUpload && displayDocUpload();
  };

  function handleToggleChange() {
    setActive(!active);
  }
  const NumberWithCommas = (number: string) => {
    const addCommas = (num: string) => {
      const cleanedStr = num.replace(/\D/g, "");
      return "$" + cleanedStr.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    };
    const formattedNumber = addCommas(number);
    return formattedNumber;
  };

  const { t } = useTranslation();
  const isSuccess =
    t(typeof message === "string" ? message : "error") === t("create_project_success");

  const customContent = modal ? (
    <Stack
      className={uploadModal}
      style={{
        flexDirection: upload ? "row" : "column",
        alignItems: upload ? "flex-start" : "center",
        borderRadius: "8px",
      }}
    >
      <div style={{display: "flex", alignItems: "center", }}>
        {upload && isSuccess ? (
          <SuccessIcon/>
        ) : (
          upload && (
            <ErrorIcon/>
          )
        )}
        {!upload && <Loader />}
        <Text
          style={{
            fontSize: "16px",
            fontFamily: "Poppins",
            paddingLeft: "15px",
            paddingTop: upload ? "" : "10px",
          }}
        >
          {upload ? t(typeof message === "string" ? message : "error") : t("creating_project")}
        </Text>
      </div>
    </Stack>
  ) : (
    <>
      <div style={{ padding: "10px 15px", overflow: "auto", maxHeight: "calc(100vh - 250px)", borderRadius: "8px" }}>
        <Stack>
          <Label required htmlFor="folderName" style={styles.label}>
            {t("project_name")}
          </Label>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", position: "relative" }}>
            <TextField
              styles={FolderNameStyles}
              borderless
              id="folderName"
              value={folderName}
              onChange={onChangeFolderNameTextField}
              placeholder={String(t("project_name"))}
            />
            <div style={{ flexShrink: 0 }}>
              <CustomToggle
                active={active}
                height="21px"
                width="78px"
                switchDotHeight="17px"
                switchDotWidth="17px"
                borderRadius="12px"
                customInactiveLeft="1.5%"
                customActiveLeft="calc(100% - 25.7%)"
                handleChange={handleToggleChange}
                redText={"Closed"}
                blueText={"Active"}
                data
              />
            </div>
          </div>
          {folderNameError && (
            <Text style={{ color: colorPallete.btRed }}>
              {t("project_name_required")}
            </Text>
          )}
          {projectExist && (
            <Text style={{ color: colorPallete.btRed }}>
              {t("project_already_exist")}
            </Text>
          )}
        </Stack>
        <Stack>
          <Label required htmlFor="project Owner" style={styles.label}>
            {t("project_owner")}
          </Label>
          <div
            style={{
              backgroundColor: "#e4e4e1",
              width: "100%",
              height: "44px",
              borderRadius: "8px",
              fontFamily: "Poppins",
              padding: "10px 14px",
              border: "1px solid #E0E0E0",
            }}
          >
            {user?.email}
          </div>
          {projectOwnerError === true && (
            <Text style={{ color: colorPallete.btRed }}>
              {t(errorMesssage)}
            </Text>
          )}
        </Stack>
        <Stack>
          <HorizontalStack
            horizontalAlign="space-between"
            style={{
              display: "flex",
              alignItems: "center",
              paddingRight: "8px",
            }}
          >
            <Label required htmlFor="counterParty" style={styles.label}>
              {t("counter_party")}
            </Label>
            <IconButton
              iconProps={{ iconName: "CircleAdditionSolid" }}
              onClick={addCounterPartyField}
              style={{
                width: "16px",
                height: "16px",
                backgroundColor: "",
                marginRight: "1%",
                color: "#2B579A",
              }}
            />
          </HorizontalStack>
          {textFields.map((value, index) => (
            <React.Fragment key={index}>
              {index !== 0 ? (
                <>
                  <HorizontalStack
                    horizontalAlign="space-between"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      paddingRight: "8px",
                    }}
                  >
                    <Label htmlFor="counterParty" style={styles.label}>
                      {t("counter_party")}
                    </Label>
                  </HorizontalStack>
                </>
              ) : null}
              <div
                key={index}
                style={{
                  display: "flex",
                  width: "100%",
                  backgroundColor: "#F8F8F8",
                  height: "47px",
                  borderRadius: "8px",
                  alignItems: "center",
                  border: "1px solid #E0E0E0",
                }}
              >
                <TextField
                  styles={CounterPartyStyles}
                  borderless
                  id="counterParty"
                  value={value}
                  onChange={(e) =>
                    handleTextFieldChange(index, e.currentTarget.value)
                  }
                  placeholder={String(t("counter_party"))}
                />
                {index !== 0 && (
                  <>
                    <svg
                      width="2"
                      height="24"
                      viewBox="0 0 2 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M1 0.5V23.5" stroke="#E0E0E0" />
                    </svg>
                    <Stack
                      onClick={() => removeTextField(index)}
                      style={{ padding: "15px", cursor: "pointer" }}
                    >
                      <svg
                        width="9"
                        height="3"
                        viewBox="0 0 9 3"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M8.09091 0.579545V2.45455H0.454545V0.579545H8.09091Z"
                          fill="#717070"
                        />
                      </svg>
                    </Stack>
                  </>
                )}
              </div>
            </React.Fragment>
          ))}
          {counterPartyError && (
            <Text style={{ color: colorPallete.btRed }}>
              {t("counter_party_required")}
            </Text>
          )}
        </Stack>
        <HorizontalStack
          style={{
            width: "100%",
            gap: "12px",
          }}
        >
          <Stack style={{ width: "50%" }}>
            <Label htmlFor="clientNumber" style={styles.label}>
              {t("client_number")}
            </Label>
            <TextField
              styles={FolderNameStyles}
              id="clientNumber"
              borderless
              inputMode="numeric"
              value={clientNumber}
              onChange={(e) => {
                const numericValue = e.currentTarget.value.replace(/\D/g, "");
                setClientNumber(numericValue);
              }}
              placeholder="000000"
            />
          </Stack>
          <Stack style={{ width: "50%" }}>
            <Label htmlFor="clientMatter" style={styles.label}>
              {t("client_matter")}
            </Label>
            <TextField
              styles={FolderNameStyles}
              id="clientMatter"
              borderless
              inputMode="numeric"
              value={clientMatter}
              onChange={(e) => {
                const numericValue = e.currentTarget.value.replace(/\D/g, "");
                setClientMatter(numericValue);
              }}
              placeholder="000000"
            />
          </Stack>
        </HorizontalStack>
        <HorizontalStack
          style={{
            width: "100%",
            gap: "12px",
          }}
        >
          <Stack style={{ width: "50%" }}>
            <Label htmlFor="estimatedClosingValue" style={styles.label}>
              {t("project_value")}
            </Label>
            <TextField
              styles={projectValueStyles}
              borderless
              inputMode="numeric"
              id="estimatedClosingValue"
              value={
                estimatedClosingValue.length === 0
                  ? "$"
                  : NumberWithCommas(estimatedClosingValue)
              }
              onChange={onChangeEstimatedClosingValueTextField}
              placeholder={"Estimated Project Value"}
            />
            {estimatedCompletetionValueError === true && (
              <Text style={{ color: colorPallete.btRed }}>
                {"Estimated Project Closing Value Is Required."}
              </Text>
            )}
          </Stack>
          <Stack style={{ width: "50%" }}>
            <Label htmlFor="completionDate" style={styles.label}>
              {t("completion_date")}
            </Label>
            <TextField
              styles={CompletionDateStyles}
              borderless
              id="completionDate"
              value={completionDate}
              onChange={onChangeDateField}
              placeholder={"MM/DD/YYYY"}
              type="date"
            />
          </Stack>
        </HorizontalStack>
        <Stack>
          <Label htmlFor="description" style={styles.descriptionLabel}>
            {t("description")}
          </Label>
          <TextField
            styles={DescriptionStyles}
            borderless
            multiline={true}
            onChange={onChangeMultiLine}
            id="description"
            value={description}
            placeholder={String(t("project_description"))}
            resizable={false}
          />
        </Stack>
      </div>
      <Stack className="horizontalLine"></Stack>
      <HorizontalStack
        horizontalAlign="end"
        styles={buttonStackStyles}
        tokens={horizontalStackTokens}
      >
        <Text
          className="createButton primaryButton"
          style={{
            ...styles.createButton,
            backgroundColor:
              folderName.length !== 0 && updatedTextFields[0].length !== 0
                ? "#18468F"
                : "#EAEAEA",
            color:
              folderName.length !== 0 && updatedTextFields[0].length !== 0
                ? "#F4F4F4"
                : "#717070",
            textAlign: "center",
          }}
          onClick={() => {
            createProject();
          }}
        >
          {t("create_project")}
        </Text>
        <Text
          className="cancelButton"
          style={{ ...styles.cancelButton, textAlign: "center" }}
          onClick={() => {
            closeProject();
          }}
          onMouseEnter={() => {}}
        >
          {t("cancel")}
        </Text>
      </HorizontalStack>
    </>
  );

  return (
    <Popup
      isOpen={props.isModalOpen}
      title={
        upload
          ? isSuccess
            ? t("success")
            : t("error")
          : t("new_project")
      }
      iconName={null}
      contentTitle=""
      content=""
      onClose={closeProject}
      customContent={customContent}
      showCloseButton={!modal}
      width="595px"
      minHeight={modal ? "149px" : "auto"}
      backgroundColor={
        upload
          ? isSuccess
            ? "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"
            : "#FCF0ED"
          : "linear-gradient(90deg, #ECF0F9 0%, #EFECF9 100%)"
      }
    />
  );
};

const styles = {
  label: {
    color: colorPallete.black1,
    fontSize: "14px",
    fontWeight: "700",
    fontFamily: "Poppins",

    lineHeight: "20px",
  },
  descriptionLabel: {
    color: colorPallete.black1,
    fontSize: "14px",
    fontWeight: "700",
    fontFamily: "Poppins",
    lineHeight: "20px",
  },
  createButton: {
    borderRadius: "4px",
    padding: "12px 24px",
    fontSize: "14px",
    lineHeight: "16px",
    fontFamily: "Poppins",
    fontWeight: "500",
    alignItems: "center",
    minWidth: "fit-content",
    height: "40px",
    gap: "10px",
    cursor: "pointer",
  },
  cancelButton: {
    backgroundColor: "#E4E8EC",
    color: colorPallete.black1,
    borderRadius: "4px",
    minWidth: "120px",
    height: "40px",
    padding: "12px 24px",
    fontSize: "14px",
    lineHeight: "16px",
    fontWeight: 500,
    fontFamily: "Poppins",
    cursor: "pointer",
  },
};
